import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Loader2, Send, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { StatusBadge, LevelBadge, VT_LABELS } from "@/components/TicketBits";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const errMsg = (e) =>
  typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Operazione non riuscita";

const Photo = ({ path, authHeader }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api
      .get(`/files/${path}`, { headers: authHeader, responseType: "blob" })
      .then((r) => {
        url = URL.createObjectURL(r.data);
        setSrc(url);
      })
      .catch(() => {});
    return () => url && URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  if (!src) return <div className="w-full h-40 rounded-xl bg-white/5 animate-pulse" />;
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" data-testid={`ticket-photo-${path.split("/").pop()}`}>
      <img src={src} alt="Foto allegata al ticket" className="w-full h-40 object-cover rounded-xl border border-white/10 hover:border-volt/50 transition-colors" />
    </a>
  );
};

export default function TicketDetail() {
  const { id } = useParams();
  const { user, loading, authHeader } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const bottomRef = useRef(null);

  const loadTicket = useCallback(() => {
    api
      .get(`/tickets/${id}`, { headers: authHeader() })
      .then((r) => setTicket(r.data))
      .catch((e) => {
        if (e?.response?.status === 403 || e?.response?.status === 404) navigate("/dashboard");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadMessages = useCallback(() => {
    api
      .get(`/tickets/${id}/messages`, { headers: authHeader() })
      .then((r) => setMessages(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!user) return;
    loadTicket();
    loadMessages();
    const t = setInterval(() => {
      loadTicket();
      loadMessages();
    }, 4000);
    return () => clearInterval(t);
  }, [user, loadTicket, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  if (loading || (!ticket && user)) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="text-volt animate-spin" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to="/accedi" replace />;
  if (!ticket) return null;

  const isOwner = ticket.cliente_id === user.user_id;
  const isExpert = user.role === "esperto";
  const chatActive = ["assigned", "in_progress"].includes(ticket.status);

  const action = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      loadTicket();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/tickets/${id}/messages`, { text: text.trim() }, { headers: authHeader() });
      setText("");
      loadMessages();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  const genDraft = async () => {
    setDraftBusy(true);
    try {
      const { data } = await api.post(`/tickets/${id}/ai-preventivo`, {}, { headers: authHeader() });
      setDraft(data.draft);
      if (data.photos_used > 0) toast.success(`Bozza generata analizzando ${data.photos_used} foto`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDraftBusy(false);
    }
  };

  const sendDraft = async () => {
    if (!draft?.trim()) return;
    setSending(true);
    try {
      await api.post(
        `/tickets/${id}/messages`,
        { text: `BOZZA PREVENTIVO (da confermare):\n\n${draft.trim()}` },
        { headers: authHeader() }
      );
      setDraft(null);
      loadMessages();
      toast.success("Bozza inviata in chat");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian" data-testid="ticket-detail-page">
      <Nav />
      <main className="px-4 sm:px-8 max-w-5xl mx-auto pt-28 pb-20">
        <button
          data-testid="ticket-back-button"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-500 hover:text-volt transition-colors mb-6"
        >
          <ArrowLeft size={14} /> Dashboard
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <div className="glass-card rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <StatusBadge status={ticket.status} />
                <LevelBadge level={ticket.level} label={ticket.level_label} />
              </div>
              <h1 className="font-display text-xl sm:text-3xl font-bold text-white" data-testid="ticket-title">
                {ticket.title}
              </h1>
              <p className="font-mono text-xs text-slate-500 mt-3">
                {VT_LABELS[ticket.vehicle_type]} {ticket.vehicle_info && `· ${ticket.vehicle_info}`} · €{ticket.price_eur}
              </p>
              {ticket.dtc_codes && (
                <p className="mt-3 font-mono text-sm text-spark" data-testid="ticket-dtc">
                  DTC: {ticket.dtc_codes}
                </p>
              )}
              <p className="mt-5 text-sm sm:text-base text-slate-300 leading-relaxed whitespace-pre-wrap" data-testid="ticket-description">
                {ticket.description}
              </p>
              {ticket.photos?.length > 0 && (
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="ticket-photos-grid">
                  {ticket.photos.map((p) => (
                    <Photo key={p} path={p} authHeader={authHeader()} />
                  ))}
                </div>
              )}
            </div>

            {isExpert && ticket.net_earnings != null && (
              <p className="font-mono text-xs text-slate-500 px-1" data-testid="ticket-net-earnings">
                Guadagno netto esperto: €{ticket.net_earnings.toFixed(2)} (piattaforma 20%)
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              {isExpert && ticket.status === "open" && (
                <button
                  data-testid="ticket-assign-button"
                  onClick={() => action(() => api.post(`/tickets/${id}/assign`, {}, { headers: authHeader() }), "Ticket preso in carico!")}
                  className="bg-volt text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-spark transition-colors"
                >
                  Prendi in carico
                </button>
              )}
              {isExpert && ticket.expert_id === user.user_id && ticket.status === "assigned" && (
                <button
                  data-testid="ticket-start-button"
                  onClick={() => action(() => api.patch(`/tickets/${id}/status`, { status: "in_progress" }, { headers: authHeader() }))}
                  className="bg-volt text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-spark transition-colors"
                >
                  Avvia consulenza
                </button>
              )}
              {isExpert && ticket.expert_id === user.user_id && ticket.status === "in_progress" && (
                <button
                  data-testid="ticket-complete-button"
                  onClick={() => action(() => api.patch(`/tickets/${id}/status`, { status: "completed" }, { headers: authHeader() }), "Consulenza completata!")}
                  className="bg-emerald-500 text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-emerald-400 transition-colors"
                >
                  Segna completata
                </button>
              )}
              {isOwner && ticket.status === "completed" && (
                <button
                  data-testid="ticket-close-button"
                  onClick={() => action(() => api.patch(`/tickets/${id}/status`, { status: "closed" }, { headers: authHeader() }), "Ticket chiuso. Grazie!")}
                  className="border border-white/20 text-slate-200 px-6 py-3 rounded-full hover:border-volt hover:text-volt transition-colors"
                >
                  Chiudi ticket
                </button>
              )}
              {isOwner && ticket.status === "pending_payment" && (
                <button
                  data-testid="ticket-pay-button"
                  onClick={() => navigate(`/nuovo-ticket?ticket_id=${id}`)}
                  className="bg-volt text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-spark transition-colors"
                >
                  Completa il pagamento — €{ticket.price_eur}
                </button>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="glass-card rounded-2xl flex flex-col h-[520px]" data-testid="ticket-chat">
              <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Chat del ticket</span>
                <div className="flex items-center gap-2">
                  {isExpert && chatActive && (
                    <button
                      data-testid="ticket-ai-draft-button"
                      onClick={genDraft}
                      disabled={draftBusy}
                      className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-spark/40 text-spark rounded-full px-3 py-1.5 hover:bg-spark hover:text-obsidian transition-colors disabled:opacity-50"
                    >
                      {draftBusy ? <Loader2 size={11} className="animate-spin" /> : null}
                      Bozza preventivo AI
                    </button>
                  )}
                  <span className={`w-2 h-2 rounded-full ${chatActive ? "bg-emerald-400 animate-pulse" : "bg-slate-600"}`} />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                {!chatActive ? (
                  <p className="text-slate-600 text-sm text-center pt-16 px-6">
                    {ticket.status === "open"
                      ? "La chat si attiva quando un esperto prende in carico il ticket."
                      : ticket.status === "pending_payment"
                        ? "Completa il pagamento per pubblicare il ticket."
                        : "La chat è chiusa."}
                  </p>
                ) : messages.length === 0 ? (
                  <p className="text-slate-600 text-sm text-center pt-16 px-6">Nessun messaggio. Scrivi il primo!</p>
                ) : (
                  messages.map((m) => {
                    const mineMsg = m.sender_id === user.user_id;
                    return (
                      <div key={m.message_id} className={`flex ${mineMsg ? "justify-end" : "justify-start"}`}>
                        <div
                          data-testid={`chat-message-${m.message_id}`}
                          className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                            mineMsg ? "bg-volt text-obsidian rounded-br-sm" : "bg-white/8 border border-white/10 text-slate-200 rounded-bl-sm"
                          }`}
                        >
                          {!mineMsg && (
                            <span className="block font-mono text-[9px] uppercase tracking-wider text-slate-500 mb-1">
                              {m.sender_name} · {m.sender_role === "esperto" ? "esperto" : "cliente"}
                            </span>
                          )}
                          {m.text}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>
              {draft !== null && (
                <div className="p-3 border-t border-spark/20 bg-spark/5" data-testid="ticket-ai-draft-panel">
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-spark mb-2">
                    Bozza AI — controlla e modifica prima di inviare
                  </p>
                  <textarea
                    data-testid="ticket-ai-draft-textarea"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="w-full min-h-[160px] bg-obsidian/60 border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-200 font-mono focus:outline-none focus:border-spark resize-y"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      data-testid="ticket-ai-draft-send"
                      onClick={sendDraft}
                      disabled={sending}
                      className="flex-1 bg-spark text-obsidian font-semibold text-xs px-4 py-2.5 rounded-full hover:scale-[1.01] transition-transform disabled:opacity-50"
                    >
                      Invia in chat
                    </button>
                    <button
                      data-testid="ticket-ai-draft-discard"
                      onClick={() => setDraft(null)}
                      className="border border-white/15 text-slate-400 text-xs px-4 py-2.5 rounded-full hover:text-slate-200 transition-colors"
                    >
                      Elimina
                    </button>
                  </div>
                </div>
              )}
              {chatActive && (
                <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
                  <input
                    data-testid="chat-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Scrivi un messaggio..."
                    className="flex-1 bg-obsidian/60 border border-white/10 rounded-full px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-volt"
                  />
                  <button
                    data-testid="chat-send-button"
                    type="submit"
                    disabled={sending || !text.trim()}
                    className="w-10 h-10 rounded-full bg-volt text-obsidian flex items-center justify-center hover:bg-spark transition-colors disabled:opacity-40"
                    aria-label="Invia messaggio"
                  >
                    <Send size={16} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
